import { useAuthStore } from '@/stores/authStore'
import { API_URL } from '@/lib/utils/constants'
import type { ChatMessage, SendChatMessageRequest, SendChatMessageResponse } from '@/types'

export interface ChatStreamHandlers {
  /** The saved user message — emitted immediately, before any generation. */
  onUserMessage?: (message: ChatMessage) => void
  /** A chunk of assistant text as it is generated. */
  onDelta?: (content: string) => void
  /** Final persisted state: conversation + both messages. */
  onDone?: (response: SendChatMessageResponse) => void
}

/**
 * POST a chat message to the SSE streaming endpoint and dispatch events as
 * they arrive. Uses fetch + ReadableStream because EventSource cannot send
 * an Authorization header or a POST body.
 */
export async function streamChatMessage(
  familyId: string,
  conversationId: string,
  data: SendChatMessageRequest,
  handlers: ChatStreamHandlers
): Promise<void> {
  const token = useAuthStore.getState().tokens?.accessToken
  const base = API_URL.endsWith('/') ? API_URL : `${API_URL}/`
  const url = new URL(
    `families/${familyId}/chat/conversations/${conversationId}/messages/stream`,
    base
  )

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
    body: JSON.stringify(data),
  })

  if (!response.ok || !response.body) {
    if (response.status === 401) {
      useAuthStore.getState().clearAuth()
    }
    throw new Error(`Chat stream failed (${response.status})`)
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  const dispatch = (block: string) => {
    let event = ''
    let dataStr = ''
    for (const line of block.split('\n')) {
      if (line.startsWith('event: ')) event = line.slice('event: '.length)
      else if (line.startsWith('data: ')) dataStr = line.slice('data: '.length)
    }
    if (!event || !dataStr) return
    const payload = JSON.parse(dataStr)
    if (event === 'user_message') handlers.onUserMessage?.(payload as ChatMessage)
    else if (event === 'delta') handlers.onDelta?.((payload as { content: string }).content)
    else if (event === 'done') handlers.onDone?.(payload as SendChatMessageResponse)
  }

  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    let separator
    while ((separator = buffer.indexOf('\n\n')) !== -1) {
      dispatch(buffer.slice(0, separator))
      buffer = buffer.slice(separator + 2)
    }
  }
  if (buffer.trim()) dispatch(buffer)
}
